import { accountPage } from "@cheatsheetz/sheetz-accounts/pages/AccountPageLazy";
import { authCallbackPage } from "@cheatsheetz/sheetz-accounts/pages/AuthCallbackPageLazy";
import { changePasswordPage } from "@cheatsheetz/sheetz-accounts/pages/ChangePasswordPageLazy";
import { confirmEmailPage } from "@cheatsheetz/sheetz-accounts/pages/ConfirmEmailPageLazy";
import { consentPage } from "@cheatsheetz/sheetz-accounts/pages/ConsentPageLazy";
import { completeEmailSigninPage } from "@cheatsheetz/sheetz-accounts/pages/EmailSignInPageLazy";
import { EmailSignInSentPage } from "@cheatsheetz/sheetz-accounts/pages/EmailSignInSentPage";
import { forgotPasswordPage } from "@cheatsheetz/sheetz-accounts/pages/ForgotPasswordPageLazy";
import { forgotPasswordSuccessPage } from "@cheatsheetz/sheetz-accounts/pages/ForgotPasswordSuccessPageLazy";
import { gettingStartedPage } from "@cheatsheetz/sheetz-accounts/pages/GettingStartedPageLazy";
import { loginInterstitialPage } from "@cheatsheetz/sheetz-accounts/pages/LoginInterstitialPageLazy";
import { loginPage } from "@cheatsheetz/sheetz-accounts/pages/LoginPageLazy";
import { organizationDetailPage } from "@cheatsheetz/sheetz-accounts/pages/OrganizationDetailPageLazy";
import { organizationsPage } from "@cheatsheetz/sheetz-accounts/pages/OrganizationsPageLazy";
import { registerPage } from "@cheatsheetz/sheetz-accounts/pages/RegisterPageLazy";
import { registrationSuccessPage } from "@cheatsheetz/sheetz-accounts/pages/RegistrationSuccessPageLazy";
import { resetPasswordPage } from "@cheatsheetz/sheetz-accounts/pages/ResetPasswordPageLazy";
import { AccountRoutes } from "@cheatsheetz/sheetz-accounts/routes";
import { cheatsheetDetailPage } from "@cheatsheetz/sheetz-cheatsheets/pages/CheatsheetDetailPageLazy";
import { cheatsheetsHubPage } from "@cheatsheetz/sheetz-cheatsheets/pages/CheatsheetsHubPageLazy";
import { cheatsheetViewPage } from "@cheatsheetz/sheetz-cheatsheets/pages/CheatsheetViewPageLazy";
import { createCheatsheetPage } from "@cheatsheetz/sheetz-cheatsheets/pages/CreateCheatsheetPageLazy";
import { CheatsheetRoutes } from "@cheatsheetz/sheetz-cheatsheets/routes";
import { ProtectedRoute } from "@cheatsheetz/sheetz-core/components/ProtectedRoute";
import { libraryPage } from "@cheatsheetz/sheetz-elements/pages/LibraryPageLazy";
import { ElementRoutes } from "@cheatsheetz/sheetz-elements/routes";
import { aboutPage } from "@cheatsheetz/sheetz-home/pages/AboutPageLazy";
import { comingSoonPage } from "@cheatsheetz/sheetz-home/pages/ComingSoonPageLazy";
import { licensesPage } from "@cheatsheetz/sheetz-home/pages/LicensesPageLazy";
import { privacyPage } from "@cheatsheetz/sheetz-home/pages/PrivacyPageLazy";
import { termsPage } from "@cheatsheetz/sheetz-home/pages/TermsPageLazy";
import { HomeRoutes } from "@cheatsheetz/sheetz-home/routes";
import { createWidgetPage } from "@cheatsheetz/sheetz-widgets/pages/CreateWidgetPageLazy";
import { widgetDetailPage } from "@cheatsheetz/sheetz-widgets/pages/WidgetDetailPageLazy";
import { widgetsHubPage } from "@cheatsheetz/sheetz-widgets/pages/WidgetsHubPageLazy";
import { widgetViewPage } from "@cheatsheetz/sheetz-widgets/pages/WidgetViewPageLazy";
import { WidgetRoutes } from "@cheatsheetz/sheetz-widgets/routes";
import { createBrowserRouter, Outlet } from "react-router-dom";
import { RootLayout } from "@/layouts/RootLayout";
import { RouteErrorBoundary } from "@/layouts/RouteErrorBoundary";
import { NotFoundPage } from "@/pages/NotFoundPage";

// ---------------------------------------------------------------------------
// Layout route that gates child routes behind authentication.
// Reuses the existing ProtectedRoute guard — once auth checks pass, the
// matched child route renders via <Outlet />.
// ---------------------------------------------------------------------------
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <Outlet />
    </ProtectedRoute>
  );
}

// ---------------------------------------------------------------------------
// Route definitions use react-router's `lazy` property so each page module
// is loaded on demand when its route matches. This keeps the initial bundle
// small and avoids top-level React.lazy() declarations.
//
// During navigation the previous page stays visible until the new module
// loads, giving a smoother experience than a Suspense spinner swap.
// ---------------------------------------------------------------------------
export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // -------------------------------------------------------------------
      // Public routes — accessible without authentication
      // -------------------------------------------------------------------
      {
        path: HomeRoutes.Home,
        lazy: comingSoonPage,
      },
      {
        path: HomeRoutes.About,
        lazy: aboutPage,
      },
      {
        path: HomeRoutes.Privacy,
        lazy: privacyPage,
      },
      {
        path: HomeRoutes.Terms,
        lazy: termsPage,
      },
      {
        path: HomeRoutes.Licenses,
        lazy: licensesPage,
      },
      {
        path: AccountRoutes.AuthCallback,
        lazy: authCallbackPage,
      },
      {
        path: AccountRoutes.Login,
        lazy: loginPage,
      },
      {
        path: AccountRoutes.LoginInterstitial,
        lazy: loginInterstitialPage,
      },
      {
        path: AccountRoutes.Register,
        lazy: registerPage,
      },
      {
        path: AccountRoutes.RegisterSuccess,
        lazy: registrationSuccessPage,
      },
      {
        path: AccountRoutes.ConfirmEmail,
        lazy: confirmEmailPage,
      },
      {
        path: AccountRoutes.EmailSignIn,
        lazy: completeEmailSigninPage,
      },
      {
        path: AccountRoutes.EmailSignInSent,
        element: <EmailSignInSentPage />,
      },
      {
        path: AccountRoutes.ForgotPassword,
        lazy: forgotPasswordPage,
      },
      {
        path: AccountRoutes.ForgotPasswordSuccess,
        lazy: forgotPasswordSuccessPage,
      },
      {
        path: AccountRoutes.ResetPassword,
        lazy: resetPasswordPage,
      },
      {
        path: AccountRoutes.Consent,
        lazy: consentPage,
      },

      // -------------------------------------------------------------------
      // Protected routes — require authentication (ProtectedLayout guard)
      // -------------------------------------------------------------------
      {
        element: <ProtectedLayout />,
        children: [
          {
            path: AccountRoutes.GettingStarted,
            lazy: gettingStartedPage,
          },
          {
            path: AccountRoutes.Account,
            lazy: accountPage,
          },
          {
            path: AccountRoutes.ChangePassword,
            lazy: changePasswordPage,
          },
          {
            path: AccountRoutes.Organizations,
            lazy: organizationsPage,
          },
          {
            path: AccountRoutes.OrganizationDetail,
            lazy: organizationDetailPage,
          },
          {
            path: WidgetRoutes.CreateWidget,
            lazy: createWidgetPage,
          },
          {
            path: WidgetRoutes.WidgetDetail,
            lazy: widgetDetailPage,
          },
          {
            path: WidgetRoutes.Widget,
            lazy: widgetViewPage,
          },
          {
            path: WidgetRoutes.Widgets,
            lazy: widgetsHubPage,
          },
          {
            path: ElementRoutes.Library,
            lazy: libraryPage,
          },
          {
            path: CheatsheetRoutes.CreateCheatsheet,
            lazy: createCheatsheetPage,
          },
          {
            path: CheatsheetRoutes.CheatsheetDetail,
            lazy: cheatsheetDetailPage,
          },
          {
            path: CheatsheetRoutes.Sheet,
            lazy: cheatsheetViewPage,
          },
          {
            path: CheatsheetRoutes.Cheatsheets,
            lazy: cheatsheetsHubPage,
          },
        ],
      },

      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
